import React, { useState, useRef, useEffect } from 'react';
import styles from './Dropdown.module.css';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  badge?: string | number;
  disabled?: boolean;
}

export interface DropdownProps {
  label?: string;
  options: DropdownOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  multiSelect?: boolean;
  clearable?: boolean;
  className?: string;
  error?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  searchable = false,
  multiSelect = false,
  clearable = true,
  className,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen]);

  const selectedValues = Array.isArray(value) ? value : [value];
  const selectedOptions = options.filter((opt) =>
    selectedValues.includes(opt.value)
  );

  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const handleOptionClick = (optionValue: string) => {
    if (multiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiSelect ? [] : '');
    setSearchQuery('');
  };

  const hasValue = multiSelect
    ? selectedValues.length > 0 && selectedValues[0] !== ''
    : value !== '';

  return (
    <div className={`${styles.dropdown} ${className || ''}`} ref={dropdownRef}>
      {label && <label className={styles.label}>{label}</label>}

      {/* Trigger button */}
      <button
        type="button"
        className={`${styles.trigger} ${isOpen ? styles.open : ''} ${
          error ? styles.error : ''
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.triggerContent}>
          {hasValue ? (
            multiSelect ? (
              <span className={styles.multipleSelected}>
                {selectedOptions.length === 1 && selectedOptions[0] ? (
                  <>
                    {selectedOptions[0].icon && (
                      <span className={styles.icon}>
                        {selectedOptions[0].icon}
                      </span>
                    )}
                    <span className={styles.selectedLabel}>
                      {selectedOptions[0].label}
                    </span>
                  </>
                ) : (
                  <span className={styles.selectedCount}>
                    {selectedOptions.length} selected
                  </span>
                )}
              </span>
            ) : selectedOptions[0] ? (
              <>
                {selectedOptions[0].icon && (
                  <span className={styles.icon}>{selectedOptions[0].icon}</span>
                )}
                <span className={styles.selectedLabel}>
                  {selectedOptions[0].label}
                </span>
                {selectedOptions[0].badge && (
                  <span className={styles.badge}>
                    {selectedOptions[0].badge}
                  </span>
                )}
              </>
            ) : null
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </span>

        <div className={styles.triggerIcons}>
          {clearable && hasValue && !disabled && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          )}
          <svg
            className={styles.chevron}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </button>

      {error && <span className={styles.errorText}>{error}</span>}

      {/* Dropdown menu */}
      {isOpen && (
        <div className={styles.menu}>
          {/* Search input if enabled */}
          {searchable && (
            <div className={styles.searchWrapper}>
              <svg
                className={styles.searchIcon}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M11.5 7a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm-.82 4.74a6 6 0 111.06-1.06l3.04 3.04a.75.75 0 11-1.06 1.06l-3.04-3.04z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="text"
                className={styles.search}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options list */}
          <ul className={styles.optionsList} role="listbox">
            {clearable && hasValue && !multiSelect && (
              <li
                className={`${styles.option} ${styles.clearOption}`}
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                  setSearchQuery('');
                }}
              >
                <span className={styles.clearText}>Clear selection</span>
              </li>
            )}

            {filteredOptions.length === 0 ? (
              <li className={styles.emptyState}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>No options found</span>
              </li>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);

                return (
                  <li
                    key={option.value}
                    className={`${styles.option} ${
                      isSelected ? styles.selected : ''
                    } ${option.disabled ? styles.disabled : ''}`}
                    onClick={() => {
                      if (!option.disabled) {
                        handleOptionClick(option.value);
                      }
                    }}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {multiSelect && (
                      <div
                        className={`${styles.checkbox} ${
                          isSelected ? styles.checked : ''
                        }`}
                      >
                        {isSelected && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                          </svg>
                        )}
                      </div>
                    )}
                    {option.icon && (
                      <span className={styles.optionIcon}>{option.icon}</span>
                    )}
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.badge && (
                      <span className={styles.optionBadge}>{option.badge}</span>
                    )}
                    {!multiSelect && isSelected && (
                      <svg
                        className={styles.checkIcon}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
